import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  canAccessAdminUi,
  canEditCatalog,
  canUpdateOrderStatus,
  hasFullAdminPowers,
  isProductionUiSandbox,
  isPlatformOwner,
  normalizeTelegramId,
} from '../adminAccess';
import {
  authCrmHintsFromApi,
  canAccessCrmUi,
  canCrmFullControl,
  hasPlatformControl,
} from '../crmAccess';
import { useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store';
import { effectivePartnerDiscountPct } from '../lib/effectiveDiscount';
import api, {
  authTelegram,
  getAdminCatalogBranches,
  getAdminCatalogSubtypes,
  getAdminManagerCrmLeaderboard,
  getAdminManagers,
  getAdminOrderCrmStats,
  getCrmAlerts,
  getAdminV2FunnelInsights,
  patchCrmOrderSalesStage,
  type AdminCatalogBranchRow,
  type AdminCatalogSubtypeRow,
  type AdminManagerBrief,
  type AdminManagerCrmLeaderboard,
  type AdminOrderCrmStats,
  type CrmAlerts,
  type V2FunnelInsights,
  recalculateCart,
} from '../api';
import { CrmClient360Panel } from '../components/admin/crm/CrmClient360';
import { CrmSalesFunnelBar } from '../components/admin/crm/CrmSalesFunnelBar';
import { CrmManagerDesk } from '../components/admin/crm/CrmManagerDesk';
import { CrmUnifiedDealDrawer } from '../components/admin/crm/CrmUnifiedDealDrawer';
import {
  crmDealSearchKeysToClear,
  crmOpenDealFromLegacySearch,
  formatCrmDealParam,
  parseCrmDealParam,
  sameCrmOpenDeal,
  type CrmOpenDeal,
  type CrmDealSnapshot,
  type CrmOpenDealHandler,
} from '../components/admin/crm/crmDealDeepLink';
import { CrmFunnelGate } from '../components/admin/crm/CrmFunnelGate';
import { AdminSystemOverviewPanel } from '../components/admin/AdminSystemOverview';
import { useCrmPipeline, useInvalidateCrm } from '../hooks/crm/useCrmQueries';
import { CrmMyDayPanel } from '../components/admin/crm/CrmMyDayPanel';
import { useAdminOrderActions } from '../hooks/admin/useAdminOrderActions';
import { AdminConfirmSheet } from '../components/admin/AdminConfirmSheet';
import { SALES_STAGE_META } from '../components/admin/crm/crmLabels';
import type { SalesStage } from '../api';
import { openTelegramUserDm } from '../openTelegramUser';
import { useHaptic } from '../hooks/useHaptic';
import {
  RefreshCw, ChevronDown, ChevronUp, Package,
  CheckCircle, Clock, Wrench, Truck, XCircle, AlertCircle,
  Users, Search, MessageCircle, Star, ShieldCheck, ShieldOff, TrendingUp, Palette, Factory,
  Kanban, Bell, CalendarClock,
} from 'lucide-react';
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure';
import { AdminHorizontalTabRail, type AdminTabRailItem } from '@/components/admin/AdminHorizontalTabRail';
import { CatalogAdminTab } from './admin/CatalogAdminTab';
import { AdminOrdersPanel } from './admin/AdminOrdersPanel';
import { CheckoutUiAdminPanel } from './admin/CheckoutUiAdminPanel';
import { ManagerCrmLeaderboardPanel } from './admin/ManagerCrmLeaderboard';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { AdminClientQuickActions } from '../components/admin/AdminClientQuickActions';
import { AdminClientDealsStrip } from '../components/admin/AdminClientDealsStrip';

// ── Types ─────────────────────────────────────────────────────────────────────
const STATUSES = [
  { value: 'pending',       label: 'Ожидает',        glassColor: 'rgba(246,173,85,0.15)',  textColor: '#F6AD55', borderColor: 'rgba(246,173,85,0.35)',  icon: Clock },
  { value: 'confirmed',     label: 'Подтверждён',    glassColor: 'rgba(99,179,237,0.15)',  textColor: '#63B3ED', borderColor: 'rgba(99,179,237,0.35)',  icon: CheckCircle },
  { value: 'in_production', label: 'В производстве', glassColor: 'rgba(159,122,234,0.15)', textColor: '#B794F4', borderColor: 'rgba(159,122,234,0.35)', icon: Wrench },
  { value: 'ready',         label: 'Готов',          glassColor: 'rgba(72,187,120,0.15)',  textColor: '#68D391', borderColor: 'rgba(72,187,120,0.35)',  icon: Package },
  { value: 'delivered',     label: 'Доставлен',      glassColor: 'rgba(255,255,255,0.06)', textColor: '#A0AEC0', borderColor: 'rgba(255,255,255,0.12)', icon: Truck },
  { value: 'cancelled',     label: 'Отменён',        glassColor: 'rgba(252,129,74,0.15)',  textColor: '#FC814A', borderColor: 'rgba(252,129,74,0.35)',  icon: XCircle },
];

const ROLE_LABELS: Record<string, { label: string; glassColor: string; textColor: string; borderColor: string }> = {
  client:  { label: 'Клиент',   glassColor: 'rgba(255,255,255,0.06)', textColor: '#A0AEC0', borderColor: 'rgba(255,255,255,0.12)' },
  partner: { label: 'Партнёр',  glassColor: 'rgba(99,179,237,0.15)',  textColor: '#63B3ED', borderColor: 'rgba(99,179,237,0.3)'  },
  manager: { label: 'Менеджер', glassColor: 'rgba(246,173,85,0.15)',  textColor: '#F6AD55', borderColor: 'rgba(246,173,85,0.3)'  },
  admin:   { label: 'Админ',    glassColor: 'rgba(159,122,234,0.15)', textColor: '#B794F4', borderColor: 'rgba(159,122,234,0.3)' },
  measurer: { label: 'Замерщик', glassColor: 'rgba(72,187,120,0.15)', textColor: '#68D391', borderColor: 'rgba(72,187,120,0.35)' },
  designer: { label: 'Дизайнер', glassColor: 'rgba(236,72,153,0.15)', textColor: '#F687B3', borderColor: 'rgba(236,72,153,0.35)' },
  production: { label: 'Производство', glassColor: 'rgba(160,174,192,0.18)', textColor: '#CBD5E0', borderColor: 'rgba(160,174,192,0.35)' },
};

// Superadmin can assign admin role

const DISCOUNT_PRESETS = [
  { label: 'Нет', value: 0 },
  { label: '10% Агент', value: 10 },
  { label: '20% Дилер', value: 20 },
  { label: '40% Демо',  value: 40 },
];

const STATUS_NOTE_TEMPLATE: Record<string, string> = {
  pending: 'Заказ принят. Менеджер проверяет детали и свяжется с вами в ближайшее время.',
  confirmed: 'Заказ подтвержден. Передали в производство.',
  in_production: 'Заказ в производстве. Сообщим, как только изделие будет готово.',
  ready: 'Изделие готово. Доступна доставка или самовывоз.',
  delivered: 'Заказ доставлен. Спасибо, что выбрали Onviz.',
  cancelled: 'Заказ отменен. Если нужно, подготовим новое предложение под ваш проект.',
};

function statusMeta(value: string) {
  return STATUSES.find(s => s.value === value) ?? STATUSES[0];
}

function pluralOrders(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'заказ';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'заказа';
  return 'заказов';
}

interface OrderUser { id: number; telegram_id: number; first_name: string; username?: string; role: string; }
interface OrderItem { id: number; product_snapshot: Record<string, any>; quantity: number; unit_price: number; total_price: number; length_m?: number; width_m?: number; }
interface AdminOrder {
  id: number;
  order_number: string;
  status: string;
  total_amount: number;
  discount_pct: number;
  final_amount: number;
  notes?: string;
  manager_notes?: string;
  assigned_manager_id?: number | null;
  assigned_manager?: { id: number; first_name?: string; username?: string } | null;
  client_assigned_manager_id?: number | null;
  created_at: string;
  updated_at?: string;
  status_history?: Array<{ at: string; from?: string; to: string; manager_notes?: string | null }>;
  user: OrderUser;
  items: OrderItem[];
}
interface AdminUser {
  id: number;
  telegram_id: number;
  username?: string;
  first_name?: string;
  role: string;
  partner_discount_pct: number;
  assigned_manager_id?: number | null;
  created_at: string;
}
interface ItemDraft { quantity: number; unit_price: number; }
interface NewItemDraft { name: string; quantity: number; unit_price: number; }


type Tab = 'dashboard' | 'funnel' | 'orders' | 'clients' | 'catalog' | 'copy';
type OrderScope = 'all' | 'mine' | 'unassigned';

function managerDisplayName(m?: { first_name?: string | null; username?: string | null } | null): string {
  if (!m) return '';
  return (m.first_name || m.username || '').trim();
}
type MeasurementInterest = 'sliding' | 'roman' | 'roller' | 'smarthome' | '';
const MEASUREMENT_SOURCE_META: Array<{ key: string; label: string }> = [
  { key: 'из каталога', label: 'Каталог' },
  { key: 'из карточки решения', label: 'Карточка решения' },
  { key: 'из карточки товара', label: 'Карточка товара' },
];
type MeasurementSourceFilter = '' | 'Каталог' | 'Карточка решения' | 'Карточка товара';
const MEASUREMENT_STATUS_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
  requested: { label: 'Принят', color: '#63B3ED', bg: 'rgba(99,179,237,0.15)', border: 'rgba(99,179,237,0.35)' },
  scheduled: { label: 'В работе', color: '#F6AD55', bg: 'rgba(246,173,85,0.15)', border: 'rgba(246,173,85,0.35)' },
  completed: { label: 'Завершён', color: '#68D391', bg: 'rgba(72,187,120,0.15)', border: 'rgba(72,187,120,0.35)' },
  cancelled: { label: 'Отменён', color: '#FC814A', bg: 'rgba(252,129,74,0.15)', border: 'rgba(252,129,74,0.35)' },
};

function extractMeasurementSourceLabel(notes?: string): string | null {
  if (!notes) return null;
  const lowered = notes.toLowerCase();
  const match = MEASUREMENT_SOURCE_META.find((item) => lowered.includes(item.key));
  return match?.label ?? null;
}
interface AdminMeasurement {
  id: number;
  status: string;
  city?: string;
  phone?: string;
  preferred_time?: string;
  interest?: string | null;
  notes?: string;
  assigned_measurer_id?: number | null;
  order_id?: number | null;
  report_notes?: string | null;
  completed_at?: string | null;
  attachment_count?: number;
  created_at?: string | null;
  user: OrderUser;
}
interface RetentionRunResult {
  ok: boolean;
  dry_run: boolean;
  sent_count: number;
  sent: Array<{ measurement_id: number; telegram_id: number; tag: string }>;
}
// ── Component ─────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const {
    token,
    role,
    userId,
    firstName,
    telegramId,
    authAdminHint,
    authFullAdminHint,
    authPlatformOwnerHint,
    authCrmScope,
    authCrmAccess,
    setDiscount,
    setAuth,
    discountPct,
  } = useAppStore();
  const crmAccessParams = useMemo(
    () => ({
      role,
      telegramId,
      serverAdminPanelHint: authAdminHint,
      serverFullAdminHint: authFullAdminHint,
      authPlatformOwnerHint,
      authCrmScope,
      authCrmAccess,
    }),
    [role, telegramId, authAdminHint, authFullAdminHint, authPlatformOwnerHint, authCrmScope, authCrmAccess],
  );
  const isSuperAdmin = hasPlatformControl(crmAccessParams);
  const crmUi = canAccessCrmUi(crmAccessParams);
  const crmFull = canCrmFullControl(crmAccessParams);
  const showAdminChrome = useMemo(
    () => canAccessAdminUi({ role, telegramId, serverAdminPanelHint: authAdminHint }),
    [role, telegramId, authAdminHint],
  );
  const catalogWrite = useMemo(
    () => canEditCatalog({ role, telegramId, serverAdminPanelHint: authAdminHint }),
    [role, telegramId, authAdminHint],
  );

  // Load all branches + subtypes for dropdown (once per session)
  useEffect(() => {
    if (!token || allBranches.length > 0) return;
    void getAdminCatalogBranches().then(async (branches) => {
      setAllBranches(branches);
      const byBranch: Record<number, AdminCatalogSubtypeRow[]> = {};
      const flat: AdminCatalogSubtypeRow[] = [];
      await Promise.all(branches.map(async (b) => {
        try {
          const subs = await getAdminCatalogSubtypes(b.id);
          byBranch[b.id] = subs;
          flat.push(...subs);
        } catch { byBranch[b.id] = []; }
      }));
      setSubtypesForBranch(byBranch);
      setAllSubtypes(flat);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const fullAdmin = useMemo(
    () => hasFullAdminPowers({
      role,
      telegramId,
      serverAdminPanelHint: authAdminHint,
      serverFullAdminHint: authFullAdminHint,
    }),
    [role, telegramId, authAdminHint, authFullAdminHint],
  );
  const orderStatusEditor = useMemo(
    () => canUpdateOrderStatus({ role, telegramId, serverAdminPanelHint: authAdminHint }),
    [role, telegramId, authAdminHint],
  );
  const isProductionSandbox = isProductionUiSandbox({ role, telegramId });

  // Available roles for assignment (superadmin can assign admin)
  const availableRoles = isSuperAdmin
    ? ['client', 'partner', 'manager', 'admin', 'measurer', 'designer', 'production']
    : fullAdmin
      ? ['client', 'partner', 'manager', 'measurer', 'designer', 'production']
      : ['client', 'partner', 'manager'];
  const canManageStaff = role === 'manager' || role === 'admin' || fullAdmin;
  const haptic = useHaptic();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState<Tab>('dashboard');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [tab]);

  useEffect(() => {
    if (!fullAdmin && tab === 'clients') {
      setTab('orders');
    }
    if (isProductionSandbox && tab !== 'orders') {
      setTab('orders');
    }
    if (!crmUi && tab === 'funnel') {
      setTab('orders');
    }
  }, [crmUi, tab, isProductionSandbox]);

  // Orders state
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [orderScope, setOrderScope] = useState<OrderScope>(() => {
    if (isPlatformOwner(telegramId) || role === 'admin') return 'all';
    if (role === 'manager') return 'unassigned';
    return 'all';
  });
  const [orderSearch, setOrderSearch] = useState('');
  const [pendingQueueCount, setPendingQueueCount] = useState(0);
  const [crmStats, setCrmStats] = useState<AdminOrderCrmStats | null>(null);
  const [crmStatsError, setCrmStatsError] = useState<string | null>(null);
  const [statsManagerId, setStatsManagerId] = useState<number | null>(null);
  const [statsUnassignedOnly, setStatsUnassignedOnly] = useState(false);
  const { data: dashboardPipeline } = useCrmPipeline({
    mineOnly: !crmFull && statsManagerId == null,
    managerId: crmFull && statsManagerId != null ? statsManagerId : null,
    includeUnassigned: true,
    enabled: crmUi && tab === 'dashboard',
  });
  const [managers, setManagers] = useState<AdminManagerBrief[]>([]);
  const [managerLeaderboard, setManagerLeaderboard] = useState<AdminManagerCrmLeaderboard | null>(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [crmAlerts, setCrmAlerts] = useState<CrmAlerts | null>(null);
  const [v2Funnel, setV2Funnel] = useState<V2FunnelInsights | null>(null);
  const [crmClient360Id, setCrmClient360Id] = useState<number | null>(null);
  const [clientDealsRefresh, setClientDealsRefresh] = useState(0);
  const [crmOpenDeal, setCrmOpenDeal] = useState<CrmOpenDeal | null>(null);
  const [crmDealSnapshot, setCrmDealSnapshot] = useState<CrmDealSnapshot | null>(null);
  const [crmFocusTasksOnOpen, setCrmFocusTasksOnOpen] = useState(false);
  const invalidateCrm = useInvalidateCrm();
  const [ordersManagerFilterId, setOrdersManagerFilterId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [updating, setUpdating] = useState<number | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [orderDiscounts, setOrderDiscounts] = useState<Record<number, number>>({});
  const [itemDrafts, setItemDrafts] = useState<Record<number, ItemDraft>>({});
  const [newItemDrafts, setNewItemDrafts] = useState<Record<number, NewItemDraft>>({});
  /** Замеры в карточке заказа: свёрнуты по умолчанию */
  const [orderMeasOpen, setOrderMeasOpen] = useState<Record<number, boolean>>({});
  /** Редактор состава: свёрнут по умолчанию (список строк всегда виден) */
  const [orderComposeEditorOpen, setOrderComposeEditorOpen] = useState<Record<number, boolean>>({});
  const [measurements, setMeasurements] = useState<AdminMeasurement[]>([]);
  const [measurementsLoading, setMeasurementsLoading] = useState(false);
  const [measurementInterest, setMeasurementInterest] = useState<MeasurementInterest>('');
  const [measurementSourceFilter, setMeasurementSourceFilter] = useState<MeasurementSourceFilter>('');
  const [measurementSearch, setMeasurementSearch] = useState('');
  const [measurementSort, setMeasurementSort] = useState<'newest' | 'city'>('newest');
  const [retentionRunning, setRetentionRunning] = useState(false);
  const [retentionResult, setRetentionResult] = useState<RetentionRunResult | null>(null);
  const [measurers, setMeasurers] = useState<Array<{ id: number; first_name?: string; username?: string }>>([]);
  const [assigningMeasurer, setAssigningMeasurer] = useState<number | null>(null);
  const [assignMeasurerMessage, setAssignMeasurerMessage] = useState<{
    measurementId: number;
    text: string;
    tone: 'success' | 'error';
  } | null>(null);

  // Clients state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [updatingUser, setUpdatingUser] = useState<number | null>(null);
  const [expandedUser, setExpandedUser] = useState<number | null>(null);
  const [showMeasurementsPanel, setShowMeasurementsPanel] = useState(false);
  const [highlightMeasurementId, setHighlightMeasurementId] = useState<number | null>(null);
  // Subtype dropdown state — passed as props to CatalogAdminTab
  const [allBranches, setAllBranches] = useState<AdminCatalogBranchRow[]>([]);
  const [allSubtypes, setAllSubtypes] = useState<AdminCatalogSubtypeRow[]>([]);
  const [subtypesForBranch, setSubtypesForBranch] = useState<Record<number, AdminCatalogSubtypeRow[]>>({});

  const fetchCrmStats = useCallback(async () => {
    if (!token) return;
    try {
      const params: { manager_id?: number; unassigned?: boolean } = {};
      if (statsUnassignedOnly) params.unassigned = true;
      else if (statsManagerId != null) params.manager_id = statsManagerId;
      const data = await getAdminOrderCrmStats(
        Object.keys(params).length > 0 ? params : undefined,
      );
      setCrmStats(data);
      setCrmStatsError(null);
    } catch (err: unknown) {
      setCrmStats(null);
      setCrmStatsError(
        formatAdminPanelError(err, 'Статистика заказов недоступна — проверьте backend / миграции'),
      );
    }
  }, [token, statsManagerId, statsUnassignedOnly]);

  const fetchCrmExtras = useCallback(async () => {
    if (!token || !crmUi) return;
    try {
      const mgrParams =
        crmFull && statsManagerId != null ? { manager_id: statsManagerId, include_unassigned: true } : {};
      const alerts = await getCrmAlerts(mgrParams);
      setCrmAlerts(alerts);
    } catch {
      setCrmAlerts(null);
    }
    if (isSuperAdmin) {
      try {
        const funnel = await getAdminV2FunnelInsights(168);
        setV2Funnel(funnel);
      } catch {
        setV2Funnel(null);
      }
    }
  }, [token, crmUi, crmFull, isSuperAdmin, statsManagerId]);

  const fetchManagers = useCallback(async () => {
    if (!token || !fullAdmin) return;
    try {
      const data = await getAdminManagers();
      setManagers(data);
    } catch {
      setManagers([]);
    }
  }, [token, fullAdmin]);

  const fetchManagerLeaderboard = useCallback(async () => {
    if (!token || !fullAdmin) return;
    setLeaderboardLoading(true);
    try {
      const data = await getAdminManagerCrmLeaderboard();
      setManagerLeaderboard(data);
    } catch {
      setManagerLeaderboard(null);
    } finally {
      setLeaderboardLoading(false);
    }
  }, [token, fullAdmin]);

  // ── Fetch orders (параллельно считаем очередь «Ожидает» для баннера и таба) ──
  const fetchOrders = useCallback(async (opts?: { soft?: boolean; forClientsPanel?: boolean }) => {
    if (!token) return;
    if (!opts?.soft) setOrdersLoading(true);
    setOrdersError(null);
    try {
      const mainParams: Record<string, string> = { limit: opts?.forClientsPanel ? '400' : '100' };
      if (!opts?.forClientsPanel) {
        if (filterStatus) mainParams.status = filterStatus;
        else mainParams.exclude_terminal = 'true';
        if (orderSearch.trim()) mainParams.search = orderSearch.trim();
        if (orderScope === 'mine') mainParams.mine = 'true';
        if (orderScope === 'unassigned') mainParams.unassigned = 'true';
        if (ordersManagerFilterId != null) mainParams.manager_id = String(ordersManagerFilterId);
      }
      const pendingParams: Record<string, string> = { status: 'pending', unassigned: 'true', limit: '200' };
      const [pendingRes, listRes] = await Promise.all([
        api.get('/admin/orders', { params: pendingParams }),
        api.get('/admin/orders', { params: mainParams }),
      ]);
      const pl = Array.isArray(pendingRes.data) ? pendingRes.data : [];
      setPendingQueueCount(pl.length);
      setOrders(Array.isArray(listRes.data) ? listRes.data : []);
    } catch (err: unknown) {
      setOrders([]);
      setPendingQueueCount(0);
      setOrdersError(
        formatAdminPanelError(
          err,
          'Не удалось загрузить заказы. Проверьте Railway backend и миграции (alembic upgrade head).',
        ),
      );
    } finally {
      if (!opts?.soft) setOrdersLoading(false);
    }
  }, [token, filterStatus, orderSearch, orderScope, ordersManagerFilterId]);

  const openOrdersForManager = useCallback((managerId: number | null) => {
    if (managerId == null) {
      setStatsManagerId(null);
      setStatsUnassignedOnly(true);
      setOrdersManagerFilterId(null);
      setOrderScope('unassigned');
    } else {
      setStatsManagerId(managerId);
      setStatsUnassignedOnly(false);
      setOrdersManagerFilterId(managerId);
      setOrderScope('all');
    }
    setFilterStatus('');
    setTab('orders');
  }, []);

  const fetchMeasurements = useCallback(async (opts?: { soft?: boolean }) => {
    if (!token) return;
    if (!opts?.soft) setMeasurementsLoading(true);
    try {
      const params: Record<string, string> = { limit: '400' };
      if (measurementInterest) params.interest = measurementInterest;
      const r = await api.get('/admin/measurements', { params });
      setMeasurements(Array.isArray(r.data) ? r.data : []);
    } catch {
      setMeasurements([]);
    } finally {
      if (!opts?.soft) setMeasurementsLoading(false);
    }
  }, [token, measurementInterest]);

  const runMeasurementRetention = async (dryRun: boolean) => {
    setRetentionRunning(true);
    try {
      const r = await api.post('/admin/retention/measurements/run', null, {
        params: { dry_run: dryRun, limit: 200 },
      });
      setRetentionResult(r.data);
      await fetchMeasurements();
    } catch {
      setRetentionResult({ ok: false, dry_run: dryRun, sent_count: 0, sent: [] });
    } finally {
      setRetentionRunning(false);
    }
  };


  const updateMeasurementStatus = async (measurementId: number, status: 'scheduled' | 'completed' | 'cancelled') => {
    setUpdating(measurementId);
    try {
      await api.patch(`/admin/measurements/${measurementId}`, { status });
      setMeasurements((prev) =>
        prev.map((m) => (m.id === measurementId ? { ...m, status } : m))
      );
    } finally {
      setUpdating(null);
    }
  };

  const assignMeasurerToRequest = async (measurementId: number, measurerUserId: number) => {
    setAssigningMeasurer(measurementId);
    setAssignMeasurerMessage(null);
    try {
      const res = await api.patch<{ assigned_measurer_id: number; status?: string }>(
        `/admin/measurements/${measurementId}/assign-measurer`,
        { measurer_user_id: measurerUserId },
      );
      setMeasurements((prev) =>
        prev.map((m) => (m.id === measurementId
          ? {
            ...m,
            assigned_measurer_id: res.data.assigned_measurer_id,
            status: res.data.status ?? (m.status === 'requested' ? 'scheduled' : m.status),
          }
          : m)),
      );
      const measurer = measurers.find((z) => z.id === measurerUserId);
      const label = measurer?.first_name || measurer?.username || 'Замерщик';
      setAssignMeasurerMessage({
        measurementId,
        text: `${label} назначен на замер`,
        tone: 'success',
      });
      void fetchMeasurements({ soft: true });
      haptic.success();
    } catch (err) {
      setAssignMeasurerMessage({
        measurementId,
        text: formatAdminPanelError(err, 'Не удалось назначить замерщика'),
        tone: 'error',
      });
    } finally {
      setAssigningMeasurer(null);
    }
  };

  const [purgeConfirmOrder, setPurgeConfirmOrder] = useState<AdminOrder | null>(null);

  const orderActions = useAdminOrderActions({
    userId,
    firstName,
    managers,
    setOrders,
    updating,
    setUpdating,
    refreshOrders: fetchOrders,
    refreshCrmStats: fetchCrmStats,
    onClaimSuccess: (orderId) => {
      setOrderScope('mine');
      setExpanded(orderId);
    },
    hapticSuccess: () => haptic.success(),
    hapticSelection: () => haptic.selection(),
  });

  const { claimOrder, assignOrderManager, purgeOrderById, actionMessage } = orderActions;

  const openCrmDeal = useCallback<CrmOpenDealHandler>(
    (deal, snapshot, options) => {
      if (!crmUi) return;
      const urlDeal = parseCrmDealParam(searchParams.get('deal'));
      if (!sameCrmOpenDeal(urlDeal, deal)) haptic.selection();
      setCrmFocusTasksOnOpen(Boolean(options?.focusTasks));
      setCrmOpenDeal((prev) => (sameCrmOpenDeal(prev, deal) ? prev : deal));
      setCrmDealSnapshot(snapshot ?? null);
      setSearchParams(
        (prev) => {
          const current = parseCrmDealParam(prev.get('deal'));
          if (sameCrmOpenDeal(current, deal)) return prev;
          const next = new URLSearchParams(prev);
          next.set('deal', formatCrmDealParam(deal));
          return next;
        },
        { replace: true },
      );
    },
    [crmUi, haptic, searchParams, setSearchParams],
  );

  const cancelCrmOrder = useCallback(
    async (
      orderId: number,
      opts?: { notifyClient: boolean; managerNote: string },
    ): Promise<boolean> => {
      if (!orderStatusEditor) return false;
      setUpdating(orderId);
      try {
        const note =
          opts?.managerNote?.trim() ||
          (notes[orderId] ?? '').trim() ||
          STATUS_NOTE_TEMPLATE.cancelled ||
          '';
        await api.patch(`/admin/orders/${orderId}`, {
          status: 'cancelled',
          manager_notes: note,
          notify_client: opts?.notifyClient === true,
        });
        setNotes((prev) => ({ ...prev, [orderId]: note }));
        setOrders((prev) =>
          prev.map((o) =>
            o.id === orderId
              ? { ...o, status: 'cancelled', manager_notes: note || undefined }
              : o,
          ),
        );
        try {
          await patchCrmOrderSalesStage(orderId, 'lost', { force: true, notifyClient: false });
        } catch {
          /* этап мог уже быть lost */
        }
        await fetchOrders({ soft: true });
        void invalidateCrm();
        haptic.success();
        return true;
      } catch {
        return false;
      } finally {
        setUpdating(null);
      }
    },
    [
      orderStatusEditor,
      notes,
      fetchOrders,
      invalidateCrm,
      haptic,
    ],
  );

  const closeCrmDeal = useCallback(() => {
    setCrmFocusTasksOnOpen(false);
    setCrmOpenDeal(null);
    setCrmDealSnapshot(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        let changed = false;
        for (const k of crmDealSearchKeysToClear()) {
          if (next.has(k)) {
            next.delete(k);
            changed = true;
          }
        }
        return changed ? next : prev;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const openDealCard = useCallback(
    (orderId: number) => {
      openCrmDeal({ kind: 'order', id: orderId });
    },
    [openCrmDeal],
  );

  const refreshCrmSurfaces = useCallback(() => {
    void fetchOrders({ soft: true });
    void fetchCrmStats();
    void invalidateCrm();
  }, [fetchOrders, fetchCrmStats, invalidateCrm]);

  const [measurementUserHighlight, setMeasurementUserHighlight] = useState<number | null>(null);

  const openMeasurementsForUser = useCallback(
    (uid: number) => {
      haptic.selection();
      setTab('orders');
      setShowMeasurementsPanel(true);
      setMeasurementUserHighlight(uid);
      setExpanded(null);
      void fetchMeasurements({ soft: true });
    },
    [haptic, fetchMeasurements],
  );

  const fetchMeasurers = useCallback(async () => {
    if (!token || !canManageStaff) return;
    try {
      const r = await api.get('/admin/measurers');
      setMeasurers(Array.isArray(r.data) ? r.data : []);
    } catch {
      setMeasurers([]);
    }
  }, [token, canManageStaff]);

  // ── Fetch users ──
  const fetchUsers = async () => {
    if (!token) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      const r = await api.get('/admin/users', { params: { limit: 200 } });
      setUsers(r.data);
    } catch {
      setUsers([]);
      setUsersError('Не удалось загрузить клиентов. Проверьте соединение и попробуйте снова.');
    }
    finally { setUsersLoading(false); }
  };



  useEffect(() => {
    void fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (!showAdminChrome) return;
    void fetchCrmStats();
  }, [showAdminChrome, fetchCrmStats, role]);

  useEffect(() => {
    if (!showAdminChrome) return;
    if (tab === 'dashboard' || tab === 'funnel') void fetchCrmExtras();
  }, [showAdminChrome, tab, fetchCrmExtras]);

  useEffect(() => {
    if (!showAdminChrome || !fullAdmin || tab !== 'dashboard') return;
    void fetchManagerLeaderboard();
    void fetchUsers();
  }, [showAdminChrome, fullAdmin, tab, fetchManagerLeaderboard]);

  /** Открытие из профиля: /admin?queue=pending */
  useEffect(() => {
    const q = searchParams.get('queue');
    if (q !== 'pending') return;
    setTab('orders');
    setFilterStatus('pending');
    setSearchParams(
      (prev) => {
        if (!prev.has('queue')) return prev;
        const next = new URLSearchParams(prev);
        next.delete('queue');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  /** Deep link: ?deal=order:ID | ?deal=lead:ID (legacy ?order= / ?lead=), ?measurements=, ?claim= */
  useEffect(() => {
    const measurementsOpen = searchParams.get('measurements') === '1';
    const measurementRaw = searchParams.get('measurement');
    const claim = searchParams.get('claim') === '1';
    if (measurementsOpen) {
      setTab('orders');
      setShowMeasurementsPanel(true);
    }
    const measurementId = measurementRaw ? Number(measurementRaw) : NaN;
    if (Number.isFinite(measurementId) && measurementId > 0) {
      setTab('orders');
      setShowMeasurementsPanel(true);
      setHighlightMeasurementId(measurementId);
    }
    const deal = crmOpenDealFromLegacySearch(searchParams);
    if (deal) {
      if (crmUi) {
        setCrmOpenDeal((prev) => (sameCrmOpenDeal(prev, deal) ? prev : deal));
      } else if (deal.kind === 'order') {
        setTab('orders');
        setExpanded(deal.id);
        if (claim && canManageStaff) void claimOrder(deal.id);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            let changed = false;
            for (const k of crmDealSearchKeysToClear()) {
              if (next.has(k)) {
                next.delete(k);
                changed = true;
              }
            }
            return changed ? next : prev;
          },
          { replace: true },
        );
      } else if (deal.kind === 'lead') {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            let changed = false;
            for (const k of crmDealSearchKeysToClear()) {
              if (next.has(k)) {
                next.delete(k);
                changed = true;
              }
            }
            return changed ? next : prev;
          },
          { replace: true },
        );
      }
    }
    if (measurementsOpen || measurementRaw) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          let changed = false;
          if (next.has('measurements')) {
            next.delete('measurements');
            changed = true;
          }
          if (next.has('measurement')) {
            next.delete('measurement');
            changed = true;
          }
          return changed ? next : prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams, canManageStaff, crmUi, claimOrder]);

  const managerFunnelBoot = useRef(false);
  useEffect(() => {
    if (managerFunnelBoot.current || !crmUi || isProductionSandbox) return;
    if (role !== 'manager') return;
    const hasDeepLink =
      Boolean(crmOpenDealFromLegacySearch(searchParams)) ||
      searchParams.get('claim') === '1' ||
      searchParams.get('queue') === 'pending';
    if (!hasDeepLink) {
      managerFunnelBoot.current = true;
      setTab('funnel');
    }
  }, [role, crmUi, isProductionSandbox, searchParams]);

  /** Автообновление заказов и замеров на вкладке «Заказы» */
  useEffect(() => {
    if (!token || tab !== 'orders') return;
    const id = window.setInterval(() => {
      void fetchOrders({ soft: true });
      if (canManageStaff) void fetchMeasurements({ soft: true });
    }, 28000);
    return () => window.clearInterval(id);
  }, [token, tab, fetchOrders, fetchMeasurements, canManageStaff]);
  useEffect(() => {
    if (!canManageStaff) return;
    if (
      tab === 'orders'
      || (fullAdmin && tab === 'clients')
      || (role === 'manager' && tab === 'dashboard')
    ) {
      void fetchMeasurements();
    }
  }, [canManageStaff, fullAdmin, role, tab, fetchMeasurements]);
  useEffect(() => {
    void fetchMeasurers();
  }, [fetchMeasurers]);
  useEffect(() => {
    void fetchManagers();
  }, [fetchManagers]);
  useEffect(() => {
    if (fullAdmin && tab === 'clients') fetchUsers();
  }, [tab, token, fullAdmin]);

  useEffect(() => {
    if (canManageStaff && tab === 'clients') {
      void fetchOrders({ soft: true, forClientsPanel: true });
    }
  }, [canManageStaff, tab, fetchOrders]);

  const ordersByUserId = useMemo(() => {
    const map = new Map<number, AdminOrder[]>();
    for (const o of orders) {
      const uid = o.user?.id;
      if (uid == null) continue;
      const list = map.get(uid);
      if (list) list.push(o);
      else map.set(uid, [o]);
    }
    for (const list of map.values()) {
      list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    return map;
  }, [orders]);

  const openOrderFromClient = useCallback(
    (order: AdminOrder) => {
      haptic.selection();
      if (crmUi) {
        openCrmDeal({ kind: 'order', id: order.id });
        return;
      }
      setTab('orders');
      setFilterStatus('');
      setOrderSearch('');
      setOrderScope('all');
      setOrdersManagerFilterId(null);
      setExpanded(order.id);
      setShowMeasurementsPanel(false);
    },
    [crmUi, haptic, openCrmDeal],
  );

  const refreshOrderAfterStatus = useCallback(() => {
    void fetchOrders({ soft: true });
    void invalidateCrm();
  }, [fetchOrders, invalidateCrm]);

  const updateOrderPricing = async (orderId: number) => {
    setUpdating(orderId);
    try {
      const discount = orderDiscounts[orderId];
      const managerNote = notes[orderId] ?? '';
      await api.patch(`/admin/orders/${orderId}/pricing`, {
        discount_pct: Number.isFinite(discount) ? discount : undefined,
        manager_notes: managerNote,
      });
      await fetchOrders();
    } finally { setUpdating(null); }
  };

  const updateOrderItem = async (orderId: number, itemId: number) => {
    setUpdating(orderId);
    try {
      const draft = itemDrafts[itemId];
      if (!draft) return;
      await api.patch(`/admin/orders/${orderId}/items/${itemId}`, {
        quantity: draft.quantity,
        unit_price: draft.unit_price,
      });
      await fetchOrders();
    } finally { setUpdating(null); }
  };

  const deleteOrderItem = async (orderId: number, itemId: number) => {
    setUpdating(orderId);
    try {
      await api.delete(`/admin/orders/${orderId}/items/${itemId}`);
      await fetchOrders();
    } finally { setUpdating(null); }
  };

  const addOrderItem = async (orderId: number) => {
    setUpdating(orderId);
    try {
      const draft = newItemDrafts[orderId] ?? { name: '', quantity: 1, unit_price: 0 };
      if (!draft.name.trim()) return;
      await api.post(`/admin/orders/${orderId}/items`, {
        name: draft.name.trim(),
        quantity: Math.max(1, Number(draft.quantity || 1)),
        unit_price: Math.max(0, Number(draft.unit_price || 0)),
      });
      setNewItemDrafts(prev => ({ ...prev, [orderId]: { name: '', quantity: 1, unit_price: 0 } }));
      await fetchOrders();
    } finally { setUpdating(null); }
  };

  const saveManagerNote = async (orderId: number, currentStatus: string) => {
    setUpdating(orderId);
    try {
      const managerNote = notes[orderId] ?? '';
      await api.patch(`/admin/orders/${orderId}`, { status: currentStatus, manager_notes: managerNote });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, manager_notes: managerNote || undefined } : o));
    } finally { setUpdating(null); }
  };

  // ── Update user ──
  const updateUser = async (
    targetUserId: number,
    patch: { role?: string; partner_discount_pct?: number; assigned_manager_id?: number | null },
  ) => {
    setUpdatingUser(targetUserId);
    try {
      const r = await api.patch(`/admin/users/${targetUserId}`, patch);
      const d = r.data as {
        admin_panel?: boolean;
        full_admin?: boolean;
        telegram_id?: number;
        assigned_manager_id?: number | null;
      };
      setUsers(prev =>
        prev.map(u =>
          u.id === targetUserId
            ? {
                ...u,
                ...patch,
                ...(d.assigned_manager_id !== undefined
                  ? { assigned_manager_id: d.assigned_manager_id }
                  : {}),
              }
            : u,
        ),
      );
      if (targetUserId === userId) {
        if (patch.partner_discount_pct !== undefined || patch.role) {
          const nextRole = patch.role ?? role;
          const nextPct = patch.partner_discount_pct ?? discountPct;
          setDiscount(effectivePartnerDiscountPct(nextRole, nextPct));
          try {
            const rec = await recalculateCart();
            setDiscount(Number(rec?.discount_pct ?? 0));
          } catch {
            /* ignore */
          }
        }
        const tidNorm = d.telegram_id != null ? normalizeTelegramId(d.telegram_id) : null;
        setAuth({
          ...(patch.role ? { role: patch.role } : {}),
          ...(tidNorm != null ? { telegramId: tidNorm } : {}),
          ...(d.admin_panel !== undefined ? { authAdminHint: Boolean(d.admin_panel) } : {}),
          ...(d.full_admin !== undefined ? { authFullAdminHint: Boolean(d.full_admin) } : {}),
        });
      }
    } finally { setUpdatingUser(null); }
  };

  const filterUsersBySearch = useCallback((list: AdminUser[]) => {
    const q = userSearch.toLowerCase().trim();
    if (!q) return list;
    return list.filter(u =>
      (u.first_name || '').toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q) ||
      String(u.telegram_id).includes(q),
    );
  }, [userSearch]);

  const filteredUsers = useMemo(() => filterUsersBySearch(users), [users, filterUsersBySearch]);

  const activeCount = orders.filter(o => !['delivered', 'cancelled'].includes(o.status)).length;
  const partnerCount = users.filter(u => u.role === 'partner').length;
  const dashboardStats = useMemo(() => {
    const doneMeasurements = measurements.filter((m) => m.status === 'completed').length;
    const pendingMeasurements = measurements.filter((m) => m.status === 'requested').length;
    if (crmStats) {
      return {
        pending: crmStats.pending,
        confirmed: crmStats.confirmed,
        inProduction: crmStats.in_production,
        ready: crmStats.ready,
        delivered: crmStats.delivered,
        cancelled: crmStats.cancelled,
        totalOrders: crmStats.total_orders,
        activeOrders: crmStats.active_orders,
        revenueTotal: crmStats.revenue_total,
        revenueMonth: crmStats.revenue_month,
        unassignedOrders: crmStats.unassigned_orders,
        doneMeasurements,
        pendingMeasurements,
      };
    }
    const pending = orders.filter((o) => o.status === 'pending').length;
    const confirmed = orders.filter((o) => o.status === 'confirmed').length;
    const inProduction = orders.filter((o) => o.status === 'in_production').length;
    const ready = orders.filter((o) => o.status === 'ready').length;
    const delivered = orders.filter((o) => o.status === 'delivered').length;
    const cancelled = orders.filter((o) => o.status === 'cancelled').length;
    const totalOrders = orders.length;
    const activeOrders = orders.filter((o) => !['delivered', 'cancelled'].includes(o.status)).length;
    const revenueTotal = orders
      .filter((o) => o.status !== 'cancelled')
      .reduce((sum, o) => sum + Number(o.final_amount || o.total_amount || 0), 0);
    const revenueMonth = orders
      .filter((o) => {
        if (o.status === 'cancelled') return false;
        const orderDate = new Date(o.created_at || 0);
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return orderDate >= monthAgo;
      })
      .reduce((sum, o) => sum + Number(o.final_amount || o.total_amount || 0), 0);
    return {
      pending,
      confirmed,
      inProduction,
      ready,
      delivered,
      cancelled,
      totalOrders,
      activeOrders,
      revenueTotal,
      revenueMonth,
      unassignedOrders: orders.filter((o) => !o.assigned_manager_id && o.status !== 'cancelled').length,
      doneMeasurements,
      pendingMeasurements,
    };
  }, [orders, measurements, crmStats]);
  const measurementsBySource = useMemo(() => {
    const totals: Record<Exclude<MeasurementSourceFilter, ''>, number> = {
      'Каталог': 0,
      'Карточка решения': 0,
      'Карточка товара': 0,
    };
    for (const m of measurements) {
      const sourceLabel = extractMeasurementSourceLabel(m.notes);
      if (sourceLabel && sourceLabel in totals) {
        totals[sourceLabel as Exclude<MeasurementSourceFilter, ''>] += 1;
      }
    }
    return totals;
  }, [measurements]);
  const filteredMeasurements = useMemo(() => {
    return measurements
      .filter((m) => {
        if (measurementUserHighlight != null && m.user.id !== measurementUserHighlight) {
          return false;
        }
        const sourceLabel = extractMeasurementSourceLabel(m.notes);
        if (measurementSourceFilter && sourceLabel !== measurementSourceFilter) {
          return false;
        }
        const q = measurementSearch.trim().toLowerCase();
        if (!q) return true;
        return (
          (m.city || '').toLowerCase().includes(q)
          || (m.phone || '').toLowerCase().includes(q)
          || (m.user.first_name || '').toLowerCase().includes(q)
          || (m.user.username || '').toLowerCase().includes(q)
          || String(m.user.telegram_id).includes(q)
          || (sourceLabel || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        if (measurementSort === 'city') {
          return (a.city || '').localeCompare(b.city || '', 'ru');
        }
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      });
  }, [measurements, measurementSourceFilter, measurementSearch, measurementSort, measurementUserHighlight]);

  const pendingMeasurementCount = useMemo(
    () => measurements.filter((m) => m.status === 'requested').length,
    [measurements],
  );

  const adminTopTabItems = useMemo((): AdminTabRailItem[] => {
    const funnelBadge = crmAlerts
      ? crmAlerts.orders_without_valid_task
        + crmAlerts.overdue_tasks
        + (crmAlerts.stale_no_activity ?? 0)
      : 0;
    const orderBadge = pendingQueueCount + pendingMeasurementCount;
    const all: AdminTabRailItem[] = [
      { id: 'dashboard', label: 'Дашборд', icon: ShieldCheck },
      { id: 'funnel', label: 'Воронка', icon: Kanban, badge: funnelBadge > 0 ? funnelBadge : undefined },
      { id: 'orders', label: 'Заказы', icon: Package, badge: orderBadge > 0 ? orderBadge : undefined },
      { id: 'clients', label: 'Клиенты', icon: Users },
      { id: 'catalog', label: 'Каталог', icon: Package },
      { id: 'copy', label: 'Тексты', icon: MessageCircle },
    ];
    if (isProductionSandbox) return all.filter((i) => i.id === 'orders');
    const managerCrm = crmUi;
    return all.filter(({ id: key }) => {
      const managerCrmTab = managerCrm && (key === 'dashboard' || key === 'funnel' || key === 'orders');
      const funnelTab = key === 'funnel' && (crmUi || fullAdmin || hasPlatformControl(crmAccessParams));
      return fullAdmin || key === 'orders' || key === 'catalog' || managerCrmTab || funnelTab;
    });
  }, [
    crmAlerts,
    crmAccessParams,
    crmUi,
    fullAdmin,
    isProductionSandbox,
    pendingMeasurementCount,
    pendingQueueCount,
  ]);

  if (!showAdminChrome) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3" style={{ color: 'var(--text-3)' }}>
        <AlertCircle size={40} /> <p className="font-medium">Нет доступа</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen pb-6">

      {/* ── Header ── */}
      <div className="px-4 pt-6 pb-0"
        style={{ background: 'linear-gradient(180deg, rgba(99,179,237,0.1) 0%, transparent 100%)', borderBottom: '1px solid var(--border-glass)' }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-xl" style={{ color: 'var(--text-1)' }}>Панель управления</h1>
              {isSuperAdmin && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(159,122,234,0.3)', color: '#D6BCFA', border: '1px solid rgba(159,122,234,0.5)' }}>
                  👑 Superadmin
                </span>
              )}
            </div>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
              {fullAdmin ? (
                <>
                  {pendingQueueCount > 0 && (
                    <span className="font-semibold" style={{ color: '#FC814A' }}>
                      Очередь: {pendingQueueCount} «Ожидает» ·{' '}
                    </span>
                  )}
                  {activeCount} в списке · {partnerCount} партнёров
                </>
              ) : role === 'manager' ? (
                <span>Мои продажи · заказы и замеры</span>
              ) : isProductionSandbox ? (
                <span>Производство · смена статусов заказов</span>
              ) : (
                <span>
                  Заказы: можно менять только <span className="font-semibold" style={{ color: 'var(--cyan-bright)' }}>состав</span>
                  {' '}(позиции, кол-во, цена строки)
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => {
              if (tab === 'funnel') {
                void fetchCrmExtras();
                return;
              }
              if (tab === 'dashboard') {
                void fetchOrders();
                void fetchCrmStats();
                void fetchCrmExtras();
                if (fullAdmin) {
                  void fetchUsers();
                  void fetchMeasurements();
                  void fetchManagerLeaderboard();
                }
                return;
              }
              if (tab === 'orders') {
                void fetchOrders();
                return;
              }
              if (tab === 'clients') {
                if (fullAdmin) fetchUsers();
                if (canManageStaff) void fetchOrders({ soft: true, forClientsPanel: true });
                return;
              }
            }}
            className="press p-2.5 rounded-full"
            style={{ background: 'rgba(99,179,237,0.1)', border: '1px solid var(--border-glass)' }}
          >
            <RefreshCw size={18} color="var(--cyan)" className={(ordersLoading || usersLoading || measurementsLoading) ? 'animate-spin' : ''} />
          </button>
        </div>

        <AdminHorizontalTabRail
          className="mb-0"
          items={adminTopTabItems}
          value={tab}
          onChange={(id) => {
            haptic.selection();
            setTab(id as Tab);
          }}
          hint="Свайп влево‑вправо — все разделы без сжатия на экране"
        />
      </div>

      {tab === 'funnel' && (
        <CrmFunnelGate
          access={crmAccessParams}
          onReauth={() => {
            haptic.selection();
            const initData = window.Telegram?.WebApp?.initData;
            if (initData) {
              void authTelegram(initData).then((data) => {
                setAuth({
                  token: data.access_token,
                  userId: data.user_id,
                  role: data.role,
                  firstName: data.first_name ?? null,
                  telegramId: data.telegram_id ?? null,
                  ...authCrmHintsFromApi(data),
                });
                setTab('funnel');
              });
            } else {
              window.location.reload();
            }
          }}
        >
          <div className="p-4">
            <CrmManagerDesk
              access={crmAccessParams}
              role={role ?? 'manager'}
              crmFull={crmFull}
              crmAlerts={crmAlerts}
              managers={managers}
              statsManagerId={statsManagerId}
              statsUnassignedOnly={statsUnassignedOnly}
              onSelectManager={(id, unassigned) => {
                if (unassigned) {
                  setStatsUnassignedOnly(true);
                  setStatsManagerId(null);
                } else {
                  setStatsUnassignedOnly(false);
                  setStatsManagerId(id);
                }
              }}
              initialOrderId={crmOpenDeal?.kind === 'order' ? crmOpenDeal.id : null}
              initialLeadId={crmOpenDeal?.kind === 'lead' ? crmOpenDeal.id : null}
              onOpenLead={(id) => openCrmDeal({ kind: 'lead', id })}
              onOpenOrder={(id) => openCrmDeal({ kind: 'order', id })}
              onOpenDeal={openCrmDeal}
              onOpenMeasurement={(id) => {
                setTab('orders');
                setShowMeasurementsPanel(true);
                setHighlightMeasurementId(id);
              }}
              onOpenClient360={(uid) => setCrmClient360Id(uid)}
              onOpenMeasurementsForUser={openMeasurementsForUser}
              hapticSelection={() => haptic.selection()}
              hapticSuccess={() => haptic.success()}
            />
          </div>
        </CrmFunnelGate>
      )}

      {tab === 'dashboard' && (
        <div className="p-4 space-y-4">

          {fullAdmin && (
            <AdminSystemOverviewPanel
              hapticSelection={() => haptic.selection()}
              hapticSuccess={() => haptic.success()}
              onResetComplete={() => {
                void fetchOrders({ soft: true });
                void fetchCrmStats();
              }}
            />
          )}

          {(ordersError || crmStatsError) && (
            <div
              className="rounded-2xl px-4 py-3 text-xs space-y-2 leading-relaxed"
              style={{ background: 'rgba(252,129,74,0.12)', border: '1px solid rgba(252,129,74,0.4)', color: '#FC814A' }}
            >
              {ordersError && <p>{ordersError}</p>}
              {crmStatsError && <p>{crmStatsError}</p>}
              <p style={{ color: 'var(--text-3)' }}>
                После деплоя backend на Railway: полностью закройте мини-апп в Telegram и откройте снова из бота.
              </p>
            </div>
          )}

          {!fullAdmin && role === 'manager' && (
            <p className="text-xs px-1" style={{ color: 'var(--text-3)' }}>
              Ваши KPI за 30 дней · нажмите «Воронка» или перейдите в «Заказы», чтобы взять заявку в работу
            </p>
          )}

          {fullAdmin && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-3)' }}>
                Срез статистики
              </p>
              <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
                <button
                  type="button"
                  onClick={() => {
                    haptic.selection();
                    setStatsManagerId(null);
                    setStatsUnassignedOnly(false);
                  }}
                  className="press shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold"
                  style={statsManagerId === null && !statsUnassignedOnly
                    ? { background: 'rgba(99,179,237,0.22)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
                    : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                >
                  Вся компания
                </button>
                {(managerLeaderboard?.managers ?? []).map((row) => {
                  const mid = row.manager?.id;
                  if (mid == null) return null;
                  const label = managerDisplayName(row.manager) || `Менеджер #${mid}`;
                  return (
                    <button
                      key={mid}
                      type="button"
                      onClick={() => {
                        haptic.selection();
                        setStatsManagerId(mid);
                        setStatsUnassignedOnly(false);
                      }}
                      className="press shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold"
                      style={statsManagerId === mid && !statsUnassignedOnly
                        ? { background: 'rgba(246,173,85,0.22)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.4)' }
                        : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {statsUnassignedOnly && (
                <p className="text-[10px] px-1" style={{ color: '#FC814A' }}>
                  KPI — только заказы без менеджера
                </p>
              )}
              {statsManagerId != null && !statsUnassignedOnly && (
                <p className="text-[10px] px-1" style={{ color: 'var(--cyan)' }}>
                  KPI и воронка — только заказы выбранного менеджера
                </p>
              )}
            </div>
          )}

          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="glass rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, rgba(99,179,237,0.15) 0%, rgba(99,179,237,0.05) 100%)', border: '1px solid rgba(99,179,237,0.3)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Package size={18} color="#63B3ED" />
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#63B3ED' }}>Заказы</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-1)' }}>{dashboardStats.totalOrders}</p>
              <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>{dashboardStats.activeOrders} активных</p>
            </div>
            <div className="glass rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, rgba(72,187,120,0.15) 0%, rgba(72,187,120,0.05) 100%)', border: '1px solid rgba(72,187,120,0.3)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Star size={18} color="#68D391" />
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#68D391' }}>Выручка</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-1)' }}>{Math.round(dashboardStats.revenueMonth / 1000)}к ₽</p>
              <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>за месяц</p>
            </div>
            {fullAdmin ? (
            <div className="glass rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, rgba(246,173,85,0.15) 0%, rgba(246,173,85,0.05) 100%)', border: '1px solid rgba(246,173,85,0.3)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Users size={18} color="#F6AD55" />
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#F6AD55' }}>Клиенты</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-1)' }}>{users.length}</p>
              <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>{partnerCount} партнёров</p>
            </div>
            ) : (
            <div className="glass rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, rgba(252,129,74,0.15) 0%, rgba(252,129,74,0.05) 100%)', border: '1px solid rgba(252,129,74,0.35)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Clock size={18} color="#FC814A" />
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#FC814A' }}>Без менеджера</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-1)' }}>{dashboardStats.unassignedOrders}</p>
              <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>можно взять в работу</p>
            </div>
            )}
            <div className="glass rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, rgba(159,122,234,0.15) 0%, rgba(159,122,234,0.05) 100%)', border: '1px solid rgba(159,122,234,0.3)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Wrench size={18} color="#B794F4" />
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#B794F4' }}>Замеры</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-1)' }}>{dashboardStats.pendingMeasurements}</p>
              <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>в обработке</p>
            </div>
          </div>

          {crmUi && !isProductionSandbox && (
            <AdminGlassDisclosure
              accent="cyan"
              defaultOpen={false}
              title="Сегодня"
              subtitle="Задачи, просрочки и сделки без действия"
              icon={<CalendarClock size={16} color="#63B3ED" />}
            >
              <CrmMyDayPanel
                onOpenDeal={openCrmDeal}
                hapticSelection={() => haptic.selection()}
              />
            </AdminGlassDisclosure>
          )}

          {(crmAlerts && (crmAlerts.orders_without_valid_task > 0 || crmAlerts.overdue_tasks > 0 || (crmAlerts.stale_no_activity ?? 0) > 0)) && (
            <AdminGlassDisclosure
              accent="coral"
              defaultOpen={false}
              title="CRM: требует внимания"
              subtitle="Без задачи, просрочка или давно не было контакта"
              icon={<Bell size={16} color="#FC814A" />}
              badge={
                crmAlerts.orders_without_valid_task +
                crmAlerts.overdue_tasks +
                (crmAlerts.stale_no_activity ?? 0)
              }
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                <div className="glass rounded-xl p-3 text-center">
                  <p className="text-xl font-black" style={{ color: '#FC814A' }}>{crmAlerts.orders_without_valid_task}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>без задачи</p>
                </div>
                <div className="glass rounded-xl p-3 text-center">
                  <p className="text-xl font-black" style={{ color: '#F6AD55' }}>{crmAlerts.overdue_tasks}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>просрочено</p>
                </div>
                {(crmAlerts.stale_no_activity ?? 0) > 0 && (
                  <div className="glass rounded-xl p-3 text-center col-span-2 sm:col-span-1">
                    <p className="text-xl font-black" style={{ color: '#B794F4' }}>{crmAlerts.stale_no_activity}</p>
                    <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>нет активности</p>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => { haptic.selection(); setTab('funnel'); }}
                className="press w-full py-2.5 rounded-xl text-xs font-bold"
                style={{ background: 'rgba(252,129,74,0.15)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}
              >
                Открыть воронку →
              </button>
            </AdminGlassDisclosure>
          )}

          {crmUi && (
            <AdminGlassDisclosure
              accent="emerald"
              defaultOpen={false}
              title="Воронка продаж (этапы)"
              subtitle="Заявка → переговоры → счёт → оплачено; тап — Kanban"
              icon={<Kanban size={16} color="#68D391" />}
            >
              <CrmSalesFunnelBar
                pipeline={dashboardPipeline ?? null}
                hapticSelection={() => haptic.selection()}
                onStageClick={() => setTab('funnel')}
              />
              {crmAlerts?.stuck_by_stage && Object.keys(crmAlerts.stuck_by_stage).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(Object.entries(crmAlerts.stuck_by_stage) as [SalesStage, number][]).map(([st, n]) => (
                    <span
                      key={st}
                      className="text-[10px] font-bold px-2 py-1 rounded-lg"
                      style={{
                        background: SALES_STAGE_META[st]?.bg ?? 'rgba(255,255,255,0.06)',
                        color: SALES_STAGE_META[st]?.color ?? 'var(--text-3)',
                      }}
                    >
                      {(SALES_STAGE_META[st]?.label ?? st)}: {n} без задачи
                    </span>
                  ))}
                </div>
              )}
            </AdminGlassDisclosure>
          )}

          {isSuperAdmin && v2Funnel && (
            <AdminGlassDisclosure
              accent="violet"
              defaultOpen={false}
              title="Конфигуратор → заказ"
              subtitle={`Окно ${v2Funnel.window_hours}ч · cart→order`}
              icon={<TrendingUp size={16} color="#D6BCFA" />}
            >
              <p className="text-2xl font-black mb-1" style={{ color: 'var(--text-1)' }}>
                {v2Funnel.totals.cart_to_order_conversion_pct}%
              </p>
              <p className="text-xs mb-2" style={{ color: v2Funnel.trend.is_drop ? '#FC814A' : '#68D391' }}>
                Δ {v2Funnel.trend.conversion_delta_pct}% за период
              </p>
              <div className="space-y-1">
                {v2Funnel.by_category.slice(0, 4).map((c) => (
                  <div key={c.category} className="flex justify-between text-[11px]">
                    <span style={{ color: 'var(--text-3)' }}>{c.category}</span>
                    <span style={{ color: c.is_drop ? '#FC814A' : '#63B3ED' }}>{c.conversion_pct}%</span>
                  </div>
                ))}
              </div>
            </AdminGlassDisclosure>
          )}

          {/* ── Production funnel (OrderStatus) ── */}
          <AdminGlassDisclosure
            accent="cyan"
            defaultOpen={false}
            title="Производство"
            subtitle="Доли по статусам заказа; тап — фильтр в списке"
            icon={<TrendingUp size={16} color="#63B3ED" />}
            badge={dashboardStats.totalOrders}
          >
            <div className="flex justify-end mb-3">
              <button
                type="button"
                onClick={() => { haptic.selection(); setTab('orders'); }}
                className="press px-3 py-1.5 rounded-lg text-[11px] font-bold"
                style={{ background: 'rgba(99,179,237,0.15)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.3)' }}
              >
                Все заказы →
              </button>
            </div>
            <div className="space-y-2">
              {[
                { key: 'pending', label: 'Ожидает', color: '#F6AD55', bg: 'rgba(246,173,85,0.2)', icon: Clock },
                { key: 'confirmed', label: 'Подтверждён', color: '#63B3ED', bg: 'rgba(99,179,237,0.2)', icon: CheckCircle },
                { key: 'inProduction', label: 'В производстве', color: '#B794F4', bg: 'rgba(159,122,234,0.2)', icon: Wrench },
                { key: 'ready', label: 'Готов', color: '#68D391', bg: 'rgba(72,187,120,0.2)', icon: Package },
                { key: 'delivered', label: 'Доставлен', color: '#A0AEC0', bg: 'rgba(160,174,192,0.2)', icon: Truck },
              ].map((step, _idx, arr) => {
                const count = dashboardStats[step.key as keyof typeof dashboardStats] || 0;
                const maxCount = Math.max(...arr.map(s => dashboardStats[s.key as keyof typeof dashboardStats] || 0), 1);
                const width = maxCount > 0 ? (count / maxCount) * 100 : 0;
                const Icon = step.icon;
                return (
                  <button
                    key={step.key}
                    onClick={() => { haptic.selection(); setTab('orders'); setFilterStatus(step.key === 'pending' ? 'pending' : step.key === 'confirmed' ? 'confirmed' : step.key === 'inProduction' ? 'in_production' : step.key === 'ready' ? 'ready' : 'delivered'); }}
                    className="press w-full flex items-center gap-3 p-3 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: step.bg }}>
                      <Icon size={18} color={step.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold" style={{ color: 'var(--text-2)' }}>{step.label}</span>
                        <span className="text-sm font-bold" style={{ color: step.color }}>{count}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(width, 5)}%`, background: step.color }} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </AdminGlassDisclosure>

          {fullAdmin && (
            <ManagerCrmLeaderboardPanel
              loading={leaderboardLoading}
              data={managerLeaderboard}
              statsManagerId={statsManagerId}
              statsUnassignedOnly={statsUnassignedOnly}
              onSelectManager={(id) => {
                setStatsManagerId(id);
                setStatsUnassignedOnly(false);
              }}
              onSelectUnassigned={() => {
                setStatsManagerId(null);
                setStatsUnassignedOnly(true);
              }}
              onOpenOrders={openOrdersForManager}
              managerLabel={managerDisplayName}
              hapticSelection={() => haptic.selection()}
            />
          )}

          {/* ── Quick Actions ── */}
          <AdminGlassDisclosure
            accent="violet"
            defaultOpen={false}
            title="Быстрые действия"
            subtitle="Жесты и переходы в один тап"
            icon={<Package size={16} color="#D6BCFA" />}
          >
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { haptic.selection(); setTab('orders'); setFilterStatus('pending'); }}
                className="press flex items-center gap-2 p-3 rounded-xl text-left"
                style={{ background: 'rgba(252,129,74,0.1)', border: '1px solid rgba(252,129,74,0.3)' }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(252,129,74,0.2)' }}>
                  <span className="text-sm font-bold" style={{ color: '#FC814A' }}>{dashboardStats.pending}</span>
                </div>
                <span className="text-xs font-semibold" style={{ color: '#FC814A' }}>Очередь ожидания</span>
              </button>
              {fullAdmin && (
              <button
                onClick={() => { haptic.selection(); setTab('clients'); }}
                className="press flex items-center gap-2 p-3 rounded-xl text-left"
                style={{ background: 'rgba(99,179,237,0.1)', border: '1px solid rgba(99,179,237,0.3)' }}
              >
                <Users size={16} color="#63B3ED" />
                <span className="text-xs font-semibold" style={{ color: '#63B3ED' }}>Клиенты</span>
              </button>
              )}
              {!fullAdmin && (
              <button
                onClick={() => { haptic.selection(); setTab('orders'); setOrderScope('unassigned'); }}
                className="press flex items-center gap-2 p-3 rounded-xl text-left"
                style={{ background: 'rgba(252,129,74,0.1)', border: '1px solid rgba(252,129,74,0.35)' }}
              >
                <ShieldCheck size={16} color="#FC814A" />
                <span className="text-xs font-semibold" style={{ color: '#FC814A' }}>Взять заказ</span>
              </button>
              )}
              <button
                onClick={() => { haptic.selection(); setTab('catalog'); }}
                className="press flex items-center gap-2 p-3 rounded-xl text-left"
                style={{ background: 'rgba(159,122,234,0.1)', border: '1px solid rgba(159,122,234,0.3)' }}
              >
                <Package size={16} color="#B794F4" />
                <span className="text-xs font-semibold" style={{ color: '#B794F4' }}>Добавить товар</span>
              </button>
              <button
                onClick={() => { haptic.selection(); setShowMeasurementsPanel(true); }}
                className="press flex items-center gap-2 p-3 rounded-xl text-left"
                style={{ background: 'rgba(72,187,120,0.1)', border: '1px solid rgba(72,187,120,0.3)' }}
              >
                <Wrench size={16} color="#68D391" />
                <span className="text-xs font-semibold" style={{ color: '#68D391' }}>Замеры</span>
              </button>
            </div>
          </AdminGlassDisclosure>
        </div>
      )}

      {tab === 'orders' && (
        <AdminOrdersPanel
          canManageStaff={canManageStaff}
          fullAdmin={fullAdmin}
          crmUi={crmUi}
          isProductionSandbox={isProductionSandbox}
          isSuperAdmin={isSuperAdmin}
          orderStatusEditor={orderStatusEditor}
          pendingMeasurementCount={pendingMeasurementCount}
          pendingQueueCount={pendingQueueCount}
          showMeasurementsPanel={showMeasurementsPanel}
          setShowMeasurementsPanel={setShowMeasurementsPanel}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          orderScope={orderScope}
          setOrderScope={setOrderScope}
          orderSearch={orderSearch}
          setOrderSearch={setOrderSearch}
          ordersManagerFilterId={ordersManagerFilterId}
          setOrdersManagerFilterId={setOrdersManagerFilterId}
          crmStats={crmStats}
          orders={orders}
          ordersLoading={ordersLoading}
          ordersError={ordersError}
          expanded={expanded}
          setExpanded={setExpanded}
          updating={updating}
          haptic={haptic}
          setTab={setTab}
          openDealCard={openDealCard}
          claimOrder={claimOrder}
          actionMessage={actionMessage}
          canPurgeOrder={isSuperAdmin}
          managers={managers}
          measurements={measurements}
          orderMeasOpen={orderMeasOpen}
          setOrderMeasOpen={setOrderMeasOpen}
          orderComposeEditorOpen={orderComposeEditorOpen}
          setOrderComposeEditorOpen={setOrderComposeEditorOpen}
          assignOrderManager={assignOrderManager}
          setPurgeConfirmOrder={setPurgeConfirmOrder}
          notes={notes}
          setNotes={setNotes}
          orderDiscounts={orderDiscounts}
          setOrderDiscounts={setOrderDiscounts}
          itemDrafts={itemDrafts}
          setItemDrafts={setItemDrafts}
          newItemDrafts={newItemDrafts}
          setNewItemDrafts={setNewItemDrafts}
          saveManagerNote={saveManagerNote}
          updateOrderPricing={updateOrderPricing}
          onOrderStatusUpdated={refreshOrderAfterStatus}
          addOrderItem={addOrderItem}
          updateOrderItem={updateOrderItem}
          deleteOrderItem={deleteOrderItem}
          measurementsPanelProps={{
            canManageStaff,
            fullAdmin,
            showMeasurementsPanel,
            setShowMeasurementsPanel,
            pendingMeasurementCount,
            retentionRunning,
            retentionResult,
            runMeasurementRetention,
            measurementInterest,
            setMeasurementInterest,
            measurementSourceFilter,
            setMeasurementSourceFilter,
            measurementsBySource,
            measurementSearch,
            setMeasurementSearch,
            measurementSort,
            setMeasurementSort,
            measurementsLoading,
            measurements,
            filteredMeasurements,
            measurers,
            assigningMeasurer,
            highlightMeasurementId,
            filterUserId: measurementUserHighlight,
            onClearFilterUserId: () => setMeasurementUserHighlight(null),
            updating,
            haptic,
            updateMeasurementStatus,
            assignMeasurerToRequest,
            assignMeasurerMessage,
            onOpenOrderDeal: openDealCard,
            onRefreshMeasurements: () => void fetchMeasurements({ soft: true }),
            viewerRole: role,
            viewerUserId: userId ?? null,
          }}
        />
      )}

      {/* ── CLIENTS TAB ── */}
      {tab === 'clients' && (
        <div className="p-4 space-y-3">
          {usersError && (
            <div className="rounded-xl px-3 py-2 text-xs" style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}>
              {usersError}
            </div>
          )}
          {/* Search */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-3)' }} />
            <input
              type="text"
              placeholder="Имя, username или Telegram ID..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              className="input-glass w-full pl-9 pr-4 py-2.5 rounded-xl text-sm"
            />
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Всего',     value: users.length,                                   textColor: 'var(--text-1)' },
              { label: 'Партнёров', value: users.filter(u => u.role === 'partner').length, textColor: 'var(--cyan-bright)' },
              { label: 'Клиентов',  value: users.filter(u => u.role === 'client').length,  textColor: 'var(--text-2)' },
            ].map(s => (
              <div key={s.label} className="glass rounded-2xl px-3 py-2.5 text-center">
                <p className="text-xl font-black" style={{ color: s.textColor }}>{s.value}</p>
                <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>{s.label}</p>
              </div>
            ))}
          </div>

          {usersLoading && (
            <div className="flex justify-center py-12">
              <RefreshCw size={24} className="animate-spin" style={{ color: 'var(--cyan)' }} />
            </div>
          )}

          {!usersLoading && filteredUsers.map(u => {
            const rl = ROLE_LABELS[u.role] ?? ROLE_LABELS.client;
            const isExpanded = expandedUser === u.id;
            const isUpdating = updatingUser === u.id;
            const name = u.first_name || u.username || `TG ${u.telegram_id}`;
            const userOrders = ordersByUserId.get(u.id) ?? [];
            const activeOrderCount = userOrders.filter(
              (o) => o.status !== 'delivered' && o.status !== 'cancelled',
            ).length;

            return (
              <div key={u.id} className="glass rounded-2xl overflow-hidden">
                <button
                  className="press w-full px-4 py-3 flex items-center gap-3 text-left"
                  onClick={() => setExpandedUser(isExpanded ? null : u.id)}
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-base"
                    style={{ background: rl.glassColor, border: `1px solid ${rl.borderColor}` }}>
                    {u.role === 'partner' ? '🏆' : u.role === 'admin' ? '⚡' : '👤'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm truncate" style={{ color: 'var(--text-1)' }}>{name}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                        style={{ background: rl.glassColor, color: rl.textColor, border: `1px solid ${rl.borderColor}` }}>{rl.label}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {u.username && <span className="text-xs" style={{ color: 'var(--text-3)' }}>@{u.username}</span>}
                      {u.partner_discount_pct > 0 && (
                        <span className="text-xs font-semibold" style={{ color: '#68D391' }}>−{u.partner_discount_pct.toFixed(0)}%</span>
                      )}
                      {canManageStaff && userOrders.length > 0 && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                          style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan)', border: '1px solid rgba(99,179,237,0.25)' }}>
                          {userOrders.length} {pluralOrders(userOrders.length)}
                          {activeOrderCount > 0 && activeOrderCount !== userOrders.length
                            ? ` · ${activeOrderCount} акт.`
                            : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  {isExpanded
                    ? <ChevronUp size={16} style={{ color: 'var(--text-3)' }} className="shrink-0" />
                    : <ChevronDown size={16} style={{ color: 'var(--text-3)' }} className="shrink-0" />}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-3 space-y-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <div className="text-xs font-mono" style={{ color: 'var(--text-3)' }}>TG ID: {u.telegram_id}</div>

                    <button
                      type="button"
                      onClick={() => {
                        haptic.selection();
                        setCrmClient360Id(u.id);
                      }}
                      className="press w-full py-2.5 rounded-xl text-xs font-bold"
                      style={{ background: 'rgba(99,179,237,0.15)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
                    >
                      Карточка CRM 360°
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        haptic.selection();
                        openTelegramUserDm(u.telegram_id, u.username);
                      }}
                      className="flex w-full items-center gap-2 text-xs font-semibold px-3 py-2 rounded-xl press"
                      style={{ background: 'rgba(99,179,237,0.1)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' }}
                    >
                      <MessageCircle size={13} /> Написать в Telegram
                    </button>

                    {canManageStaff && (
                      <>
                        <AdminClientDealsStrip
                          userId={u.id}
                          refreshKey={clientDealsRefresh}
                          hapticSelection={haptic.selection}
                          onOpenDeal={(orderId) => {
                            void fetchOrders({ soft: true, forClientsPanel: true });
                            void invalidateCrm();
                            if (crmUi) openCrmDeal({ kind: 'order', id: orderId });
                          }}
                        />
                        <AdminClientQuickActions
                          userId={u.id}
                          hapticSelection={haptic.selection}
                          hapticSuccess={haptic.success}
                          onDealsChanged={() => setClientDealsRefresh((k) => k + 1)}
                          onOrderCreated={(orderId) => {
                            void fetchOrders({ soft: true, forClientsPanel: true });
                            void invalidateCrm();
                            setClientDealsRefresh((k) => k + 1);
                            if (crmUi) openCrmDeal({ kind: 'order', id: orderId });
                          }}
                          onMeasurementCreated={(measurementId, orderId) => {
                            void fetchMeasurements({ soft: true });
                            void invalidateCrm();
                            setClientDealsRefresh((k) => k + 1);
                            if (crmUi) openCrmDeal({ kind: 'order', id: orderId });
                            else {
                              setTab('orders');
                              setShowMeasurementsPanel(true);
                              setHighlightMeasurementId(measurementId);
                              setMeasurementUserHighlight(u.id);
                            }
                          }}
                        />
                      </>
                    )}

                    {managers.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--cyan)' }}>
                          Постоянный менеджер
                        </p>
                        <select
                          className="input-glass w-full rounded-xl text-xs py-2"
                          value={u.assigned_manager_id ?? ''}
                          disabled={isUpdating}
                          onChange={(e) => {
                            const v = e.target.value;
                            void updateUser(u.id, { assigned_manager_id: v === '' ? null : Number(v) });
                          }}
                        >
                          <option value="">Не назначен</option>
                          {managers.map((m) => (
                            <option key={m.id} value={m.id}>
                              {managerDisplayName(m) || `Менеджер #${m.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}


                    {canManageStaff && (
                    <AdminGlassDisclosure
                      accent="cyan"
                      density="compact"
                      defaultOpen={false}
                      title="Заказы клиента"
                      subtitle={
                        userOrders.length === 0
                          ? 'В CRM пока нет заказов'
                          : activeOrderCount > 0
                            ? `${activeOrderCount} активных из ${userOrders.length}`
                            : `${userOrders.length} в истории`
                      }
                      icon={<Package size={15} color="#63B3ED" />}
                      badge={userOrders.length}
                    >
                      {userOrders.length === 0 ? (
                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Заказов от этого клиента пока нет.</p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {userOrders.slice(0, 20).map((order) => {
                            const om = statusMeta(order.status);
                            const OmIcon = om.icon;
                            return (
                              <button
                                key={order.id}
                                type="button"
                                onClick={() => openOrderFromClient(order)}
                                className="press w-full flex items-center gap-2 rounded-xl px-2.5 py-2 text-left"
                                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
                              >
                                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                                  style={{ background: om.glassColor, border: `1px solid ${om.borderColor}` }}>
                                  <OmIcon size={12} style={{ color: om.textColor }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[11px] font-bold truncate" style={{ color: 'var(--text-1)' }}>{order.order_number}</p>
                                  <p className="text-[10px] truncate" style={{ color: 'var(--text-3)' }}>
                                    {new Date(order.created_at).toLocaleDateString('ru-RU')}
                                    {order.assigned_manager ? ` · ${managerDisplayName(order.assigned_manager)}` : ''}
                                  </p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-[11px] font-bold" style={{ color: 'var(--cyan-bright)' }}>
                                    {order.final_amount.toLocaleString('ru-RU')} ₽
                                  </p>
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                                    style={{ background: om.glassColor, color: om.textColor, border: `1px solid ${om.borderColor}` }}>
                                    {om.label}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                          {userOrders.length > 20 && (
                            <p className="text-[10px] text-center pt-1" style={{ color: 'var(--text-3)' }}>
                              Ещё {userOrders.length - 20} в разделе «Заказы»
                            </p>
                          )}
                        </div>
                      )}
                    </AdminGlassDisclosure>
                    )}

                    <AdminGlassDisclosure
                      accent="emerald"
                      defaultOpen={false}
                      density="compact"
                      title="Замеры клиента"
                      subtitle="История заявок"
                      icon={<Wrench size={15} color="#68D391" />}
                      badge={measurements.filter((mm) => mm.user.id === u.id).length}
                    >
                      {measurements.filter((mm) => mm.user.id === u.id).length === 0 ? (
                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Заявок на замер в системе пока нет.</p>
                      ) : (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {measurements.filter((mm) => mm.user.id === u.id).slice(0, 15).map((mm) => {
                            const msm = MEASUREMENT_STATUS_META[mm.status] || {
                              label: mm.status,
                              color: 'var(--text-2)',
                              bg: 'rgba(255,255,255,0.08)',
                              border: 'rgba(255,255,255,0.18)',
                            };
                            return (
                              <div key={mm.id} className="flex items-center justify-between gap-2 text-[11px]">
                                <span style={{ color: 'var(--text-1)' }}>#{mm.id}</span>
                                <span style={{ color: 'var(--text-3)' }} className="truncate flex-1 text-right">{mm.city || '—'}</span>
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0" style={{ background: msm.bg, color: msm.color, border: `1px solid ${msm.border}` }}>{msm.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </AdminGlassDisclosure>

                    <AdminGlassDisclosure
                      accent="violet"
                      defaultOpen={false}
                      density="compact"
                      title="Роль и скидка"
                      subtitle="Права доступа и дилерская сетка"
                      icon={<ShieldCheck size={15} color="#D6BCFA" />}
                    >
                      <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>Роль</p>
                      <div className="grid grid-cols-2 gap-2">
                        {availableRoles.map(r => {
                          const rm = ROLE_LABELS[r];
                          return (
                            <button
                              key={r}
                              disabled={u.role === r || isUpdating}
                              onClick={() => { haptic.selection(); updateUser(u.id, { role: r }); }}
                              className="press flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-40"
                              style={u.role === r
                                ? { background: rm.glassColor, color: rm.textColor, border: `1px solid ${rm.borderColor}` }
                                : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }
                              }
                            >
                              {r === 'partner' ? <ShieldCheck size={12} /> : r === 'designer' ? <Palette size={12} /> : r === 'production' ? <Factory size={12} /> : <ShieldOff size={12} />}
                              {rm.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] font-bold uppercase tracking-widest mb-2 mt-3" style={{ color: 'var(--cyan)' }}>Скидка партнёра</p>
                      <div className="grid grid-cols-4 gap-1.5">
                        {DISCOUNT_PRESETS.map(p => (
                          <button
                            key={p.value}
                            disabled={u.partner_discount_pct === p.value || isUpdating}
                            onClick={() => { haptic.selection(); updateUser(u.id, { partner_discount_pct: p.value }); }}
                            className="press py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-40"
                            style={u.partner_discount_pct === p.value
                              ? { background: 'rgba(72,187,120,0.2)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)' }
                              : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }
                            }
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </AdminGlassDisclosure>

                    {isUpdating && (
                      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-3)' }}>
                        <RefreshCw size={12} className="animate-spin" /> Сохраняем...
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {!usersLoading && filteredUsers.length === 0 && !usersError && (
            <div className="text-center py-8 text-sm" style={{ color: 'var(--text-3)' }}>
              По вашему запросу клиенты не найдены.
            </div>
          )}
        </div>
      )}

      {tab === 'copy' && fullAdmin && (
        <CheckoutUiAdminPanel />
      )}

      {tab === 'catalog' && (
        <div className="p-4 overflow-x-hidden">
          <CatalogAdminTab
            token={token!}
            catalogWrite={!!catalogWrite}
            allBranches={allBranches}
            allSubtypes={allSubtypes}
            subtypesForBranch={subtypesForBranch}
          />

        </div>
      )}

      {purgeConfirmOrder && (
        <AdminConfirmSheet
          title="Удалить заказ навсегда"
          description={`Безвозвратно удалить ${purgeConfirmOrder.order_number} и связанные CRM-записи.`}
          confirmLabel="Удалить"
          expectedPhrase={purgeConfirmOrder.order_number}
          danger
          onCancel={() => setPurgeConfirmOrder(null)}
          onConfirm={async () => {
            const ok = await purgeOrderById(purgeConfirmOrder);
            if (ok) {
              setExpanded(null);
              setPurgeConfirmOrder(null);
            }
          }}
        />
      )}

      {crmUi && crmOpenDeal && (
        <CrmUnifiedDealDrawer
          key={formatCrmDealParam(crmOpenDeal)}
          deal={crmOpenDeal}
          snapshot={crmDealSnapshot}
          focusTasksOnOpen={crmFocusTasksOnOpen}
          access={crmAccessParams}
          managers={managers}
          canCancelOrder={orderStatusEditor}
          canEditOrderStatus={orderStatusEditor}
          onCancelOrder={cancelCrmOrder}
          onClose={closeCrmDeal}
          onUpdated={refreshCrmSurfaces}
          hapticSelection={() => haptic.selection()}
          hapticSuccess={() => haptic.success()}
          onNavigateMeasurement={(id) => {
            closeCrmDeal();
            setTab('orders');
            setShowMeasurementsPanel(true);
            setHighlightMeasurementId(id);
          }}
          onNavigateClient360={(uid) => {
            closeCrmDeal();
            setCrmClient360Id(uid);
          }}
          onOpenMeasurementsForUser={openMeasurementsForUser}
        />
      )}

      {crmClient360Id != null && (
        <CrmClient360Panel
          userId={crmClient360Id}
          access={crmAccessParams}
          onClose={() => setCrmClient360Id(null)}
          hapticSelection={() => haptic.selection()}
          onOpenDeal={(orderId) => {
            setCrmClient360Id(null);
            void invalidateCrm();
            openCrmDeal({ kind: 'order', id: orderId });
          }}
        />
      )}
    </div>
  );
}
